import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AllocationState, EmployeeList, EmployeeRef, EmployeeStatus, EmploymentContractStatus, EntityList, UtilisationMonth } from '@mnc/contracts';
import { api } from '../../lib/api';
import { pick } from '../../i18n';
import { cn } from '../../components/ui';
import { stripBidiMarks } from '../../lib/text';

type Tone = 'neutral' | 'brand' | 'warn' | 'danger' | 'info';
export const EMPLOYEE_STATUSES: EmployeeStatus[] = ['active', 'on_hold', 'left'];
export const EMPLOYEE_TONE: Record<EmployeeStatus, Tone> = { active: 'brand', on_hold: 'warn', left: 'neutral' };
export const STATE_TONE: Record<AllocationState, Tone> = { planned: 'info', active: 'brand', ended: 'neutral' };
export const CONTRACT_TONE: Record<EmploymentContractStatus, Tone> = { draft: 'info', active: 'brand', ended: 'neutral', cancelled: 'neutral' };
export const ROLES = ['consultant', 'executive', 'admin'] as const;
/** 0 = Sunday … 6 = Saturday, as the API stores them. */
export const WEEK = [0, 1, 2, 3, 4, 5, 6];

export function Avatar({ person, size = 40 }: { person: Pick<EmployeeRef, 'display_name_en' | 'avatar_url'>; size?: number }) {
  return (
    <span className="flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-50 font-semibold text-brand-700 dark:bg-brand-700/30 dark:text-brand-100" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {person.avatar_url ? <img src={person.avatar_url} alt="" className="h-full w-full object-cover" /> : person.display_name_en.slice(0, 1).toUpperCase()}
    </span>
  );
}

/** A person in one line: avatar, name in the page language, job title. */
export function PersonLine({ person, size = 28 }: { person: EmployeeRef; size?: number }) {
  const { i18n } = useTranslation();
  const title = pick(i18n.language, person.job_title_en ?? '', person.job_title_ar ?? '');
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar person={person} size={size} />
      <span className="min-w-0">
        <span className="block truncate font-medium" dir="auto">{pick(i18n.language, person.display_name_en, person.display_name_ar)}</span>
        {title && <span className="block truncate text-xs text-neutral-500" dir="auto">{title}</span>}
      </span>
    </span>
  );
}

const toneOf = (percent: number) => (percent > 100 ? 'bg-red-500' : percent >= 80 ? 'bg-brand-500' : percent > 0 ? 'bg-blue-500' : 'bg-neutral-300');

/** Share of a person's time that is taken; the bar stops at 100 and turns red above it. */
export function UtilisationBar({ percent, className }: { percent: number; className?: string }) {
  return (
    <span className={cn('flex items-center gap-2', className)}>
      <span className="h-1.5 min-w-12 flex-1 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800"><span className={cn('block h-full rounded-full', toneOf(percent))} style={{ width: `${Math.min(100, percent)}%` }} /></span>
      <span className={cn('w-10 shrink-0 text-end text-xs tabular-nums', percent > 100 ? 'font-semibold text-red-600' : 'text-neutral-600 dark:text-neutral-300')}>{percent}%</span>
    </span>
  );
}

/** Twelve months as columns: the peak of each month against the 100% line. */
export function UtilisationChart({ months }: { months: UtilisationMonth[] }) {
  const { t, i18n } = useTranslation();
  const top = Math.max(120, ...months.map((m) => m.peak_percent));
  const label = (month: string) => new Date(`${month}-01T00:00:00Z`).toLocaleDateString(i18n.language, { month: 'short', year: '2-digit', timeZone: 'UTC' });
  return (
    <div>
      <div className="relative flex h-32 items-end gap-1" dir="ltr">
        <div className="pointer-events-none absolute inset-x-0 border-t border-dashed border-neutral-400" style={{ bottom: `${(100 / top) * 100}%` }}><span className="absolute -top-4 end-0 text-[10px] text-neutral-500">100%</span></div>
        {months.map((m) => (
          <div key={m.month} className="flex h-full min-w-0 flex-1 flex-col justify-end" title={`${label(m.month)} · ${t('people.peak')} ${m.peak_percent}% · ${t('people.average')} ${m.average_percent}%`}>
            {m.peak_percent > 0 && <span className={cn('mb-0.5 text-center text-[10px] tabular-nums', m.over_allocated ? 'font-semibold text-red-600' : 'text-neutral-500')}>{m.peak_percent}</span>}
            <div className={cn('rounded-t', toneOf(m.peak_percent))} style={{ height: `${(m.peak_percent / top) * 100}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-1 text-[10px] text-neutral-500" dir="ltr">{months.map((m) => <span key={m.month} className="min-w-0 flex-1 truncate text-center">{label(m.month)}</span>)}</div>
    </div>
  );
}

/** Employees as select options (the staff list is small: one page of 200). */
export function useEmployeeOptions(enabled = true) {
  const { i18n } = useTranslation();
  const query = useQuery({ queryKey: ['people', 'employees', 'options'], queryFn: () => api.get<EmployeeList>('/api/people/employees?page_size=200&sort=name_en'), staleTime: 60_000, enabled });
  const options = useMemo(() => (query.data?.items ?? []).filter((e) => e.status !== 'left').map((e) => ({ value: e.id, label: pick(i18n.language, e.display_name_en, e.display_name_ar), description: e.employee_no })), [query.data, i18n.language]);
  return { options, items: query.data?.items ?? [] };
}

/** Entities of the directory as select options; closed entities take no new allocation. */
export function useEntityOptions(enabled = true) {
  const { i18n } = useTranslation();
  const query = useQuery({ queryKey: ['entities', 'options'], queryFn: () => api.get<EntityList>('/api/entities?page_size=200&sort=name_en'), staleTime: 60_000, enabled });
  const options = useMemo(() => (query.data?.items ?? []).filter((e) => e.status !== 'closed').map((e) => ({ value: e.id, label: pick(i18n.language, e.name_en, e.name_ar), description: e.slug })), [query.data, i18n.language]);
  return { options };
}

export const addDaysIso = (iso: string, days: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
/** The same day `months` later; the 31st becomes the last day of a shorter month (as the server counts). */
export const addMonthsIso = (iso: string, months: number) => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const last = new Date(Date.UTC(y, m - 1 + months + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + months, Math.min(d, last))).toISOString().slice(0, 10);
};
/** A contract's length in whole months, the default length of its renewal (as the server counts). */
export const termMonths = (start: string, end: string) => Math.max(1, Math.round((Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000) + 1) / 30.4375));

export const fmtDate = (iso: string | null, language: string, fallback = '—') => (iso ? stripBidiMarks(new Date(`${iso}T00:00:00Z`).toLocaleDateString(language, { dateStyle: 'medium', timeZone: 'UTC' })) : fallback);
